import { useState, useEffect, useMemo } from "react";
import TxnsTView from "./TxnsTView";
import TxnsTableView from "./TxnsTableView";
import { TableShimmer, ShimmerLoader, HeaderShimmer } from "components";
import { transactionAPI } from "services";
import useUniversalStore from "store/universalStore";
import { formatMoney, debounce } from "utils";

const TxnList = ({ animation, setAnimation, hideClosed, handleToggle }) => {
  const { transactions, selectedMonth, txnViewMode } = useUniversalStore((state) => state);
  const write = useUniversalStore((state) => state.write);
  const [openBalance, setOpenBalance] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter transactions based on hideClosed toggle
  const filteredTnx = useMemo(() => {
    if (hideClosed) {
      return transactions?.filter((item) => !(item.get ? item.get("cancelled") : item.cancelled));
    }
    return transactions;
  }, [hideClosed, transactions]);

  // Calculate daily cash in/out locally
  const dailyCash = useMemo(() => {
    let cashIn = 0;
    let cashOut = 0;

    transactions?.forEach(txn => {
      const isCancelled = typeof txn.get === "function" ? txn.get("cancelled") : txn.cancelled;
      if (!isCancelled) {
        const amount = (typeof txn.get === "function" ? txn.get("amount") : txn.amount) || 0;
        if (amount > 0) cashIn += amount;
        else cashOut += Math.abs(amount);
      }
    });

    return {
      in: formatMoney(cashIn),
      out: formatMoney(cashOut),
      totalDelta: cashIn - cashOut
    };
  }, [transactions]);

  // Derive closing balance from opening balance and daily delta
  const closeBalance = useMemo(() => {
    return openBalance + dailyCash.totalDelta;
  }, [openBalance, dailyCash.totalDelta]);

  const txnsWithBalance = useMemo(() => {
    let running = openBalance;
    const sorted = [...(filteredTnx || [])].sort((a, b) => {
      const dateA = new Date(typeof a?.get === "function" ? a.get("date") : a?.date || 0);
      const dateB = new Date(typeof b?.get === "function" ? b.get("date") : b?.date || 0);
      return dateA - dateB;
    });

    return sorted.map((txn) => {
      const isCancelled = typeof txn?.get === "function" ? txn.get("cancelled") : txn?.cancelled;
      const amount = (typeof txn?.get === "function" ? txn.get("amount") : txn?.amount) || 0;
      if (!isCancelled) {
        running += amount;
      }
      return {
        txn,
        balance: running,
      };
    });
  }, [filteredTnx, openBalance]);

  // Combined and debounced data fetching
  const debouncedLoadAllData = useMemo(() => {
    const fetchFunc = async (cDate) => {
      if (!cDate) return;
      try {
        const openDate = new Date(cDate.getFullYear(), cDate.getMonth(), cDate.getDate(), 0, 0, 0, 0);

        const [txnsResult, balanceResult] = await Promise.all([
          transactionAPI.getTransactionsByDate(cDate),
          transactionAPI.sumTillDate(openDate)
        ]);

        write({ transactions: txnsResult });
        setOpenBalance(balanceResult);
      } catch (error) {
        console.error("Error loading Cashbook data:", error);
      } finally {
        setLoading(false);
      }
    };
    return debounce(fetchFunc, 800);
  }, []);

  useEffect(() => {
    setLoading(true);
    debouncedLoadAllData(selectedMonth);
  }, [selectedMonth]);

  const shimmerLoaderView = () => {
    return (
      <div className="row p-0 m-0 w-100">
        <div className="col-6 col-md-6 list-group list-group-flush scrollbar-design h-100 px-md-3">
          <div className="shimmer-container">
            <HeaderShimmer />
            {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
          </div>
        </div>
        <div className="col-6 col-md-6 list-group list-group-flush scrollbar-design h-100 px-md-3">
          <div className="shimmer-container">
            <HeaderShimmer />
            {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`row p-0 m-0 mb-auto pb-5 ${animation}`}
      onAnimationEnd={() => setAnimation("")}>
      {loading ? (
        txnViewMode === "table" ? (
          <TableShimmer
            hasAccountColumn={true}
            colReceivedTitle="Cash In"
            colGivenTitle="Cash Out"
            height="calc(100vh - 200px)"
          />
        ) : (
          shimmerLoaderView()
        )
      ) : txnViewMode === "table" ? (
        <TxnsTableView
          txns={txnsWithBalance}
          openBalance={openBalance}
          closeBalance={closeBalance}
          dailyCash={dailyCash}
          setLoading={setLoading}
          handleToggle={handleToggle}
          selectedMonth={selectedMonth}
        />
      ) : (
        <TxnsTView
          filteredTnx={filteredTnx}
          dailyCash={dailyCash}
          openBalance={openBalance}
          closeBalance={closeBalance}
          setLoading={setLoading}
          handleToggle={handleToggle}
        />
      )}
    </div>
  );
};

export default TxnList;